import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ordersAPI, workOrdersAPI, machinesAPI } from '../../services/api';
import { generateNativePdf, printStyles, CompanyLetterhead } from './shared';

export function PlanningSheetPrintView() {
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Use getPlanning endpoint - same as Gantt chart for consistent data
        const [planningRes, machineRes] = await Promise.all([
          workOrdersAPI.getPlanning(),
          machinesAPI.list({ is_active: true })
        ]);
        
        // Extract work orders from planning data
        const planningData = planningRes.data || {};
        const swimlanes = planningData.swimlanes || [];
        const unassigned = planningData.unassigned || [];
        
        // Flatten all work orders from swimlanes + unassigned
        let allWorkOrders = [...unassigned];
        swimlanes.forEach(lane => {
          if (lane.work_orders && Array.isArray(lane.work_orders)) {
            allWorkOrders = [...allWorkOrders, ...lane.work_orders];
          }
        });
        
        // If no work orders from planning, fall back to list endpoint
        if (allWorkOrders.length === 0) {
          const listRes = await workOrdersAPI.list({ limit: 500 });
          allWorkOrders = listRes.data?.data || listRes.data?.items || [];
        }
        
        setWorkOrders(allWorkOrders);
        setMachines(machineRes.data || []);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading && workOrders.length > 0) {
      setTimeout(() => window.print(), 500);
    }
  }, [loading, workOrders]);

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Filter to show active WOs (include scheduled status as well)
  const activeWOs = workOrders.filter(wo => 
    ['draft', 'scheduled', 'released', 'wip', 'in_progress'].includes(wo.status?.toLowerCase())
  );

  // Sort: Primary by Machine, Secondary by Target Delivery Date
  const sortedWOs = [...activeWOs].sort((a, b) => {
    // Primary: Group by machine name
    const machineA = (a.machine_name || '').toLowerCase();
    const machineB = (b.machine_name || '').toLowerCase();
    if (machineA !== machineB) {
      return machineA.localeCompare(machineB);
    }
    
    // Secondary: Sort by target delivery date (earliest first)
    const dateA = a.target_delivery_date || a.planned_end_date || a.planned_start_date || '';
    const dateB = b.target_delivery_date || b.planned_end_date || b.planned_start_date || '';
    return dateA.localeCompare(dateB);
  });

  // Group by machine for visual separation
  const groupedByMachine = {};
  sortedWOs.forEach(wo => {
    const machine = wo.machine_name || 'Unassigned';
    if (!groupedByMachine[machine]) {
      groupedByMachine[machine] = [];
    }
    groupedByMachine[machine].push(wo);
  });

  return (
    <div className="print-container landscape" style={{ maxWidth: '297mm', margin: '0 auto', padding: '15px', fontFamily: 'Arial, sans-serif' }}>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .planning-table { width: 100%; border-collapse: collapse; font-size: 9px; }
        .planning-table th, .planning-table td { border: 1px solid #333; padding: 4px 6px; }
        .planning-table th { background: #1e40af; color: white; font-weight: bold; text-align: center; }
        .planning-table td { text-align: left; }
        .header-row { background: #e0e0e0; font-weight: bold; }
      `}</style>

      {/* Branded Header */}
      <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '3px solid #1e40af', paddingBottom: '10px' }}>
        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#1e40af' }}>SINGHANIA INTERNATIONAL LIMITED</h1>
        <div style={{ fontSize: '10px', color: '#666', marginTop: '3px' }}>
          C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India
        </div>
        <h2 style={{ margin: '10px 0 0', fontSize: '16px', fontWeight: 'bold' }}>PRODUCTION PLANNING SHEET</h2>
        <div style={{ fontSize: '10px', color: '#666' }}>
          Generated: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* Planning Table */}
      <table className="planning-table">
        <thead>
          <tr>
            <th style={{ width: '8%' }}>M/C</th>
            <th style={{ width: '7%' }}>Plan Date</th>
            <th style={{ width: '10%' }}>WO No</th>
            <th style={{ width: '12%' }}>Item (Part No)</th>
            <th style={{ width: '8%' }}>Size</th>
            <th style={{ width: '10%' }}>RM Grade</th>
            <th style={{ width: '8%' }}>Wire Size</th>
            <th style={{ width: '10%' }}>Supply Cond</th>
            <th style={{ width: '10%' }}>Tool Status</th>
            <th style={{ width: '8%' }}>Qty</th>
            <th style={{ width: '9%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {sortedWOs.length === 0 ? (
            <tr>
              <td colSpan="11" style={{ textAlign: 'center', padding: '20px' }}>No active work orders</td>
            </tr>
          ) : (
            Object.entries(groupedByMachine).map(([machineName, machineWOs]) => (
              <React.Fragment key={machineName}>
                {/* Machine Group Header */}
                <tr className="header-row">
                  <td colSpan="11" style={{ background: '#e0e0e0', fontWeight: 'bold', fontSize: '10px' }}>
                    Machine: {machineName} ({machineWOs.length} orders)
                  </td>
                </tr>
                {/* Work Orders for this machine */}
                {machineWOs.map((wo) => (
                  <tr key={wo.id}>
                    <td style={{ fontWeight: 'bold' }}>{wo.machine_name || '-'}</td>
                    <td>{formatDate(wo.planned_start_date)}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{wo.wo_number}</td>
                    <td>{wo.part_internal_no}</td>
                    <td>{wo.rm_size || '-'}</td>
                    <td>{wo.rm_grade || '-'}</td>
                    <td>{wo.wire_size || '-'}</td>
                    <td>{wo.supply_condition || '-'}</td>
                    <td>{wo.header_tool_status || wo.tool_status || '-'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{wo.qty_target?.toLocaleString()}</td>
                    <td>
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '8px',
                        fontWeight: 'bold',
                        background: wo.status === 'wip' ? '#fef3c7' : wo.status === 'released' ? '#dbeafe' : wo.status === 'scheduled' ? '#d1fae5' : '#f3f4f6',
                        color: wo.status === 'wip' ? '#92400e' : wo.status === 'released' ? '#1e40af' : wo.status === 'scheduled' ? '#065f46' : '#374151'
                      }}>
                        {wo.status?.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      {/* Summary */}
      <div style={{ marginTop: '15px', fontSize: '10px', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <strong>Total Work Orders:</strong> {sortedWOs.length} | 
          <strong> Draft:</strong> {sortedWOs.filter(wo => wo.status === 'draft').length} | 
          <strong> Scheduled:</strong> {sortedWOs.filter(wo => wo.status === 'scheduled').length} | 
          <strong> Released:</strong> {sortedWOs.filter(wo => wo.status === 'released').length} | 
          <strong> WIP:</strong> {sortedWOs.filter(wo => ['wip', 'in_progress'].includes(wo.status?.toLowerCase())).length}
        </div>
        <div>
          <strong>Total Qty:</strong> {sortedWOs.reduce((sum, wo) => sum + (wo.qty_target || 0), 0).toLocaleString()} pcs
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: '#666' }}>
        www.sturdfix.com | www.singhaniainternational.com
      </div>
    </div>
  );
}



// ============== ORDER PRODUCTION REPORT PRINT VIEW ==============

